import { useCallback, useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import Paginator, { usePaged } from "@/components/Paginator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, ExternalLink, Ban, RotateCcw } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import { useSearch } from "@/lib/search";

// Module scope: an inline array is a new reference every render and defeats the memo.
const CERT_FIELDS = ["full_name", "employee_code", "certificate_number", "title", "category"];
// The same, for what the search is handed while the register has not arrived.
const EMPTY = [];

// Certificate numbers run SPPL/TRN/2026/0009 — the collator in SortableTh is numeric-aware,
// so 0009 sorts before 0010 rather than after it the way plain string order would.
const CERT_SORT = {
  number: "certificate_number", employee: "full_name", training: "title",
  category: "category", date: "training_date",
};
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, canWrite } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/utils";
import SortableTh, { useSorted } from "@/components/SortableTh";
import { fmtDate } from "@/lib/dates";

export default function Certificates() {
  const { user } = useAuth();
  const writable = canWrite(user);
  // null until the register arrives. It started as [], so a request that never came back
  // read "0 certificates in the register." beside an Excel button exporting every one.
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("");
  const [revoking, setRevoking] = useState(null);
  const [reissuing, setReissuing] = useState(null);
  const [reason, setReason] = useState("");
  // A request out right now. Without it, Try again left the error and its button up until the
  // server answered, so a press that was working looked like one that did nothing.
  const [busy, setBusy] = useState(false);
  const loadSeq = useRef(0);
  // A failed reload keeps the last good list: after a revoke or a re-issue the rows stay up,
  // and the error row above them says they may be out of date.
  const load = useCallback(async () => {
    // Mount, Try again and the reload after every revoke and re-issue can be out at once. Only
    // the newest may answer: a Try again landing after a revoke's reload put the withdrawn
    // certificate back as valid, PDF link and Revoke button with it, and an older failure
    // landing last called rows that had just loaded out of date.
    const mine = ++loadSeq.current;
    setBusy(true);
    try {
      const { data } = await api.get("/certificates");
      if (mine !== loadSeq.current) return;
      if (!Array.isArray(data)) throw new Error("unexpected response");
      setItems(data);
      setLoadError("");
    } catch (e) {
      if (mine !== loadSeq.current) return;
      setLoadError(formatApiError(e.response?.data?.detail, "The certificate register could not be loaded."));
    }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reissue = async () => {
    try {
      const r = await api.post(`/certificates/${reissuing.id}/reissue`);
      toast.success(`${r.data.replaced} replaced by ${r.data.certificate_number}`);
      setReissuing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not re-issue")); }
  };

  const revoke = async () => {
    try {
      await api.post(`/certificates/${revoking.id}/revoke`, { reason });
      toast.success(`${revoking.certificate_number} revoked`);
      setRevoking(null); setReason(""); load();
    } catch (e) {
      // Left open on failure so the reason typed is not thrown away.
      toast.error(formatApiError(e.response?.data?.detail, "Could not revoke"));
    }
  };
  // Trimmed: an employee code pasted out of a spreadsheet usually arrives with a
  // trailing space, and matched nothing at all.
  const filtered = useSearch(items || EMPTY, q, CERT_FIELDS);
  const sorted = useSorted(filtered, CERT_SORT, sort);

  const paged = usePaged(sorted);


  return (
    <div className="space-y-5" data-testid="certificates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Certification register</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Certificates Issued</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {Array.isArray(items) ? `${items.length} certificates in the register.` : loadError && !busy ? "Not loaded." : "Loading..."}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.open(`${API}/exports/certificates.xlsx`)} data-testid="export-certs-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
      </div>
      <Card className="p-4 border border-border">
        <SearchInput value={q} onChange={setQ} testid="certs-search"
                     placeholder="Search by name, code, certificate number or training..." />
      </Card>
      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-zebra">
          <thead className="bg-muted/70 border-b border-border">
            <tr className="text-left">
              <SortableTh sortKey="number" sort={sort} onSort={setSort}>Cert #</SortableTh>
              <SortableTh sortKey="employee" sort={sort} onSort={setSort}>Employee</SortableTh>
              <SortableTh sortKey="training" sort={sort} onSort={setSort}>Training</SortableTh>
              <SortableTh sortKey="category" sort={sort} onSort={setSort}>Category</SortableTh>
              <SortableTh sortKey="date" sort={sort} onSort={setSort}>Date</SortableTh>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loadError && busy && (
              <tr data-testid="certs-retrying">
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading...</td>
              </tr>
            )}
            {loadError && !busy && (
              <tr data-testid="certs-load-error">
                <td colSpan={6} className="px-4 py-6 text-center">
                  <div className="text-destructive-text">{loadError}</div>
                  {items?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      The rows below are the register as it last loaded, and may be out of date.
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="mt-3" onClick={load} data-testid="certs-retry">
                    Try again
                  </Button>
                </td>
              </tr>
            )}
            {paged.slice.map((c) => (
              // Keyed by the number, not the id: a superseded certificate has no
              // participant row to point at, so every one of them carries id null and
              // React saw them all as the same child — "duplicated and/or omitted", its
              // words. The number is the one thing guaranteed unique across both the
              // live rows and certificate_history, because never reusing it is the whole
              // reason that table exists.
              <tr key={c.certificate_number} className={`border-b border-border last:border-0 ${c.revoked_at ? "text-muted-foreground" : ""}`} data-testid={`cert-row-${c.certificate_number}`}>
                <td className={`px-4 py-2 font-mono text-xs ${c.revoked_at ? "line-through" : ""}`}>{c.certificate_number}</td>
                <td className="px-4 py-2 font-medium">{c.full_name} <span className="text-xs text-muted-foreground">{c.employee_code}</span></td>
                <td className="px-4 py-2">{c.title}</td>
                <td className="px-4 py-2">{c.category}</td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(c.training_date)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {c.revoked_at ? (
                    // The number stays in the register on purpose — a gap in a
                    // sequential series is what an auditor would ask about.
                    <>
                      <span className="text-xs" title={`${c.revoked_reason} · ${formatDateTime(c.revoked_at)}`} data-testid={`cert-revoked-${c.id || c.certificate_number}`}>
                        {c.superseded ? `Replaced by ${c.replaced_by}` : "Revoked"}
                      </span>
                      {/* Withdrawing used to be the end of it: the number stayed on the row
                          and issuing skipped anyone who already had one, so a certificate
                          revoked in error could never be replaced. */}
                      {writable && !c.superseded && (
                        <Button variant="ghost" size="sm" className="ml-2 h-7 px-2"
                                onClick={() => setReissuing(c)} data-testid={`reissue-${c.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Re-issue
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <a href={`${API}/certificates/${c.id}/pdf`} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 text-xs hover:underline"><ExternalLink className="w-3 h-3" /> PDF</a>
                      {writable && (
                        <Button variant="ghost" size="sm" className="ml-2 text-destructive-text h-7 px-2"
                                onClick={() => { setRevoking(c); setReason(""); }} data-testid={`revoke-${c.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        <Paginator paged={paged} label="certificates" />
      </Card>
      <AlertDialog open={!!reissuing} onOpenChange={(o) => { if (!o) setReissuing(null); }}>
        <AlertDialogContent data-testid="reissue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace {reissuing?.certificate_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              {reissuing?.full_name} receives a new certificate for “{reissuing?.title}”, with the
              next number in the register. {reissuing?.certificate_number} is not reused and does
              not disappear — anyone checking it sees that it was replaced, and by which number, so a
              copy already in circulation keeps answering honestly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reissue} data-testid="reissue-confirm">Issue a replacement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!revoking} onOpenChange={(v) => { if (!v) { setRevoking(null); setReason(""); } }}>
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke {revoking?.certificate_number}</DialogTitle>
            <DialogDescription>
              Issued to {revoking?.full_name} for “{revoking?.title}”. The certificate stops
              verifying and can no longer be downloaded. The number is not reused, and this
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is this being revoked?" data-testid="revoke-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRevoking(null); setReason(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={revoke} disabled={!reason.trim()} data-testid="revoke-confirm">Revoke</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
